import React, { FC, useEffect, useState } from 'react';

// i18n
import { useTranslation } from '../../translations/useTranslation';

// Components
import { CgiPoller } from '../../components/cgi';
import { CircleLoader } from '../../components/common';
import { PowerOfAttorneyForm } from '../../components/forms';

// Models
import {
  CgiAuthenticationOption,
  FormType,
  PollingConfig,
} from '../../models/CGI/CGI';
import { PowerOfAttorneyPageContent } from '../../models/Responses/PowerOfAttorneyPageContent';
import { PowerOfAttorneyFormValue } from '../../models/Forms/PowerOfAttorneyFormValue';
import { PageView } from '../../models/PageView';

// Utils
import { getProviderFromCgiAuthenticationOption } from '../../utilities/cgi';
import { scrollToTop } from '../../utilities/utils';

// Services
import CgiService from '../../services/cgiService';
import {
  createPowerOfAttorneyCase,
  getPowerOfAttorneyPageData,
} from '../../services/formService';

// Context
import { useGlobalState } from '../../context/global-context/hooks';

// Hooks
import { usePrevious } from '../../hooks/usePrevious';

var cgiService = new CgiService();
const defaultAuthenticationOption = CgiAuthenticationOption.BankIdSameDevice;

const initialValues: PowerOfAttorneyFormValue = {
  fullName: '',
  email: '',
  emailRepeated: '',
  phone: '',
  authenticationOption: defaultAuthenticationOption,
};

const PowerOfAttorneyFormPage: FC = () => {
  const [view, setView] = useState<PageView>(
    'loading'
  );
  const { prev: prevView } = usePrevious<PageView>(view);
  const [formState, setFormState] =
    useState<PowerOfAttorneyFormValue>(initialValues);
  const [pageContent, setPageContent] = useState<PowerOfAttorneyPageContent>();
  const [pollingConfig, setPollingConfig] = useState<PollingConfig>({
    authenticationOption: defaultAuthenticationOption,
  });

  const { t, keys } = useTranslation();
  const { state } = useGlobalState();

  useEffect(() => {
    // Scroll to top when view changes
    if (!prevView || prevView === 'loading') return;
    scrollToTop();
  }, [view]);

  useEffect(() => {
    getPowerOfAttorneyPageData(state.pageId).then((response) => {
      setView('form');
      setPageContent(response.data);
    });
  }, []);

  const handleSubmit = async (values: PowerOfAttorneyFormValue) => {
    // Update form state
    setFormState(values);
    const provider = getProviderFromCgiAuthenticationOption(
      values.authenticationOption
    );

    try {
      // Initalize CGI polling process
      const response = await cgiService.init({
        provider: provider,
        isSign: true,
        signMessage: t(keys.powerOfAttorneyFormCgiSignText),
      });
      setView('poller');
      setPollingConfig({
        collectRequest: {
          target: FormType.PowerOfAttorney,
          orderRef: response.data.OrderRef,
          qrStartToken: response.data.QrStartToken,
          requestStartedMilliseconds: response.data.RequestStartedMilliseconds,
          provider: provider,
        },
        authenticationOption: values.authenticationOption,
        autoStartToken: response.data.AutoStartToken,
      });
    } catch (err) {
      // TODO: Custom error messages depending on status code?
      setView('error');
    }
  };

  const handleAuthSuccessful = async (accessToken: string) => {
    try {
      const response = await createPowerOfAttorneyCase({
        fullName: formState.fullName,
        phone: formState.phone,
        email: formState.email,
        umbracoPageId: state.pageId,
        accessKey: accessToken,
      });
      if (response.data.ThankYouPageUrl) {
        location.href = response.data.ThankYouPageUrl;
      } else {
        resetForm();
        resetView();
      }
    } catch (err) {
      // TODO: Custom error messages depending on status code?
      setView('error');
    }
  };

  const resetView = () => {
    setView('form');
  };

  const resetForm = () => {
    setFormState(initialValues);
  };

  return (
    <div className="form-app__power-of-attorney-form">
      {view === 'form' && (
        <PowerOfAttorneyForm
          formValues={formState}
          onSubmit={handleSubmit}
          modalContent={pageContent?.PowerOfAttorneyModalContent}
          information={pageContent?.PowerOfAttorneyInformation}
        />
      )}
      {view === 'loading' && <CircleLoader size="sm" />}
      {view === 'poller' && (
        <CgiPoller
          collectRequest={pollingConfig.collectRequest}
          authenticationOption={pollingConfig.authenticationOption}
          autoStartToken={pollingConfig.autoStartToken}
          onSuccess={handleAuthSuccessful}
          onReset={resetView}
        />
      )}
      {view === 'error' && (
        <div>
          <h2>{t(keys.uiErrorTitle)}...</h2>
          <p>{t(keys.resumedMemberShipFormErrorMessage)}</p>
          <button
            className="button form-app__button form-app__button--auto-width"
            onClick={resetView}
          >
            {t(keys.uiTryAgain)}
          </button>
        </div>
      )}
    </div>
  );
};

export default PowerOfAttorneyFormPage;
